/**
 * ==============================================================================
 * SECUREBANK SOC DASHBOARD - CENTRO DE OPERACIONES DE SEGURIDAD
 * ==============================================================================
 * Dashboard principal para SecureBank (La Caixa) Security Operations Center
 * Análisis de cobertura MITRE ATT&CK con métricas de seguridad en tiempo real
 * Framework multi-cloud: Azure, GCP, IBM Cloud, OCI
 * Version: 2.0 | Date: 2025-07-28
 */

import React, { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import ATTACKMatrix from '../components/ATTACKMatrix';
import TechniqueModal from '../components/TechniqueModal';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

/**
 * DashboardPage Component
 * Manages the main dashboard view with coverage matrix and analytics
 */
const DashboardPage = () => {
    // State management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [matrixData, setMatrixData] = useState(null);
    const [selectedTechniqueId, setSelectedTechniqueId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Get API client from hook
    const { apiClient } = useApi();

    /**
     * Fetch coverage matrix data from the API with enhanced error handling
     */
    const fetchCoverageMatrix = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('🔍 Cargando matriz de cobertura MITRE ATT&CK...');
            
            // API call to get coverage matrix
            const response = await apiClient.get('/api/dashboard/coverage-matrix');
            
            console.log('📊 Respuesta API:', response.status, response.data?.success);
            
            if (response.data && response.data.success && response.data.data) {
                setMatrixData(response.data.data);
                console.log('✅ Matriz cargada exitosamente:', {
                    tactics: response.data.data.matrix?.tactics?.length || 0,
                    techniques: response.data.data.matrix?.techniques?.length || 0
                });
            } else {
                console.error('❌ Error en estructura de respuesta:', response.data);
                throw new Error(response.data?.message || 'Error al cargar matriz de cobertura');
            }
        } catch (err) {
            console.error('💥 Error crítico cargando dashboard:', err);
            setError(`Error de conexión: ${err.message || 'Error desconocido'}`);
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount and when refresh key changes
    useEffect(() => {
        fetchCoverageMatrix();
    }, [refreshKey]);

    /**
     * Handle technique click from the matrix
     * @param {string} techniqueId - The ID of the clicked technique
     */
    const handleTechniqueClick = (techniqueId) => {
        console.log('Technique clicked:', techniqueId);
        setSelectedTechniqueId(techniqueId);
    };

    /**
     * Handle modal close
     */
    const handleModalClose = () => {
        setSelectedTechniqueId(null);
    };

    /**
     * Handle refresh request
     */
    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    // Render loading state - Enhanced with SecureBank branding
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md">
                    <div className="mb-6">
                        <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">SecureBank SOC</h2>
                        <p className="text-sm font-medium text-blue-600">La Caixa - Centro de Operaciones</p>
                    </div>
                    <LoadingSpinner size="large" />
                    <div className="mt-6 space-y-2">
                        <p className="text-gray-700 font-medium">Inicializando Centro de Seguridad</p>
                        <p className="text-sm text-gray-500">Cargando MITRE ATT&CK Framework</p>
                        <p className="text-xs text-gray-400">Azure • GCP • IBM Cloud • OCI</p>
                    </div>
                </div>
            </div>
        );
    }

    // Render error state - Enhanced with better UX
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
                <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-lg">
                    <div className="mb-6">
                        <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.08 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">SecureBank SOC</h2>
                        <p className="text-sm font-medium text-red-600">Error de Conexión</p>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-red-800 mb-2">⚠️ Error de Sistema</h3>
                        <p className="text-red-700 text-sm mb-3">{error}</p>
                        <p className="text-xs text-red-600">Verifique la conexión de red y el estado de los servicios</p>
                    </div>
                    
                    <button
                        onClick={handleRefresh}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium py-3 px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                        🔄 Reintentar Conexión
                    </button>
                    
                    <p className="mt-4 text-xs text-gray-500">
                        Si el problema persiste, contacte al equipo técnico
                    </p>
                </div>
            </div>
        );
    }

    // Extract data for easier access
    const { matrix, summary, metadata } = matrixData || {};

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            {/* Enhanced Header Section */}
            <div className="bg-white shadow-lg border-b border-blue-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center">
                        <div className="mb-6 lg:mb-0">
                            <div className="flex items-center mb-3">
                                <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                    </svg>
                                </div>
                                <div>
                                    <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent">
                                        Centro de Operaciones de Seguridad
                                    </h1>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <p className="text-xl text-blue-600 font-bold">SecureBank</p>
                                        <span className="text-gray-400">•</span>
                                        <p className="text-lg text-gray-600 font-medium">La Caixa</p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                                <p className="text-sm text-blue-800 font-medium">
                                    🌐 Framework MITRE ATT&CK v15.1 - Cobertura Multi-Cloud Enterprise
                                </p>
                                <p className="text-xs text-blue-600 mt-1">
                                    Azure Active Directory • Google Cloud Platform • IBM Cloud • Oracle Cloud Infrastructure
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex flex-col space-y-3">
                            <button
                                onClick={handleRefresh}
                                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg hover:shadow-xl transition-all duration-200"
                            >
                                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Actualizar Datos
                            </button>
                            
                            <div className="text-center text-xs text-gray-500">
                                🔄 Última actualización: {new Date().toLocaleTimeString('es-ES')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Enhanced Summary Metrics Section */}
            {summary && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">📋 Métricas de Seguridad Empresarial</h2>
                        <p className="text-gray-600">Análisis en tiempo real del estado de seguridad multi-cloud</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Coverage Percentage Card - Enhanced */}
                        <div className="bg-white overflow-hidden shadow-xl rounded-2xl border-l-4 border-blue-500 hover:shadow-2xl transition-shadow duration-300">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <dt className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                        Cobertura Multi-Cloud
                                    </dt>
                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                        </svg>
                                    </div>
                                </div>
                                <dd className="text-4xl font-bold text-blue-600 mb-2">
                                    {summary.overview.coveragePercentage}%
                                </dd>
                                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                                    <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{width: `${summary.overview.coveragePercentage}%`}}></div>
                                </div>
                                <p className="text-sm text-gray-700 font-medium">
                                    {summary.overview.coveredTechniques} de {summary.overview.totalTechniques} técnicas
                                </p>
                                <p className="text-xs text-blue-600 mt-2 font-medium">
                                    🌍 Azure • GCP • IBM • OCI
                                </p>
                            </div>
                        </div>

                        {/* Critical Gaps Card - Enhanced */}
                        <div className="bg-white overflow-hidden shadow-xl rounded-2xl border-l-4 border-red-500 hover:shadow-2xl transition-shadow duration-300">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <dt className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                        Gaps Críticos
                                    </dt>
                                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                                <dd className="text-4xl font-bold text-red-600 mb-2">
                                    {summary.riskDistribution.critical}
                                </dd>
                                <div className="bg-red-50 rounded-lg p-2 mb-3">
                                    <p className="text-xs text-red-800 font-semibold">⚠️ Atención Inmediata</p>
                                </div>
                                <p className="text-sm text-gray-700 font-medium">
                                    Vulnerabilidades expuestas
                                </p>
                                <p className="text-xs text-red-600 mt-2 font-medium">
                                    🔴 Riesgo Crítico Detectado
                                </p>
                            </div>
                        </div>

                        {/* Active Controls Card - Enhanced */}
                        <div className="bg-white overflow-hidden shadow-xl rounded-2xl border-l-4 border-green-500 hover:shadow-2xl transition-shadow duration-300">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <dt className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                        Controles Activos
                                    </dt>
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                                <dd className="text-4xl font-bold text-green-600 mb-2">
                                    {summary.controlStats.passingControls}
                                </dd>
                                <div className="bg-green-50 rounded-lg p-2 mb-3">
                                    <p className="text-xs text-green-800 font-semibold">✅ Estado Operativo</p>
                                </div>
                                <p className="text-sm text-gray-700 font-medium">
                                    Controles implementados
                                </p>
                                <p className="text-xs text-green-600 mt-2 font-medium">
                                    🛡️ Protección Activa
                                </p>
                            </div>
                        </div>

                        {/* AI Confidence Card - Enhanced */}
                        <div className="bg-white overflow-hidden shadow-xl rounded-2xl border-l-4 border-purple-500 hover:shadow-2xl transition-shadow duration-300">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <dt className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                        Confianza IA
                                    </dt>
                                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                                        </svg>
                                    </div>
                                </div>
                                <dd className="text-4xl font-bold text-purple-600 mb-2">
                                    {Math.round(summary.controlStats.avgConfidence * 100)}%
                                </dd>
                                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                                    <div className="bg-purple-600 h-2 rounded-full transition-all duration-500" style={{width: `${Math.round(summary.controlStats.avgConfidence * 100)}%`}}></div>
                                </div>
                                <p className="text-sm text-gray-700 font-medium">
                                    Precisión de mapeo AI
                                </p>
                                <p className="text-xs text-purple-600 mt-2 font-medium">
                                    🤖 Llama 3.1 + Vectorize
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Enhanced Coverage Matrix Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white shadow-2xl rounded-2xl overflow-hidden">
                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                            <div className="mb-4 lg:mb-0">
                                <h2 className="text-3xl font-bold text-white mb-2">
                                    🎯 Matriz de Cobertura MITRE ATT&CK
                                </h2>
                                <p className="text-gray-300 text-lg">
                                    Framework Enterprise para Ciberseguridad Multi-Cloud
                                </p>
                                <div className="flex items-center space-x-4 mt-3">
                                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">v15.1</span>
                                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">14 Tácticas</span>
                                    <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">695 Técnicas</span>
                                </div>
                            </div>
                            <div className="bg-gray-700 rounded-lg p-4">
                                <div className="text-right">
                                    <div className="text-sm text-gray-300 mb-1">
                                        🔄 Última actualización
                                    </div>
                                    <div className="text-white font-mono text-sm">
                                        {metadata ? new Date(metadata.lastUpdated).toLocaleString('es-ES') : '--'}
                                    </div>
                                    <div className="text-xs text-gray-400 mt-1">
                                        ⚡ Procesamiento: {metadata?.processingTime || '--'}ms
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Enhanced Legend */}
                    <div className="p-6">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                🎨 Leyenda de Cobertura de Seguridad
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="flex items-center bg-white p-3 rounded-lg shadow-sm border border-green-200">
                                    <div className="w-6 h-6 bg-green-700 rounded-lg mr-3 shadow-sm"></div>
                                    <div>
                                        <span className="text-gray-800 font-semibold block">Excelente</span>
                                        <span className="text-xs text-gray-600">2+ controles activos</span>
                                    </div>
                                </div>
                                <div className="flex items-center bg-white p-3 rounded-lg shadow-sm border border-green-100">
                                    <div className="w-6 h-6 bg-green-500 rounded-lg mr-3 shadow-sm"></div>
                                    <div>
                                        <span className="text-gray-800 font-semibold block">Buena</span>
                                        <span className="text-xs text-gray-600">1 control activo</span>
                                    </div>
                                </div>
                                <div className="flex items-center bg-white p-3 rounded-lg shadow-sm border border-yellow-200">
                                    <div className="w-6 h-6 bg-yellow-500 rounded-lg mr-3 shadow-sm"></div>
                                    <div>
                                        <span className="text-gray-800 font-semibold block">Parcial</span>
                                        <span className="text-xs text-gray-600">Controles inactivos</span>
                                    </div>
                                </div>
                                <div className="flex items-center bg-white p-3 rounded-lg shadow-sm border border-red-200">
                                    <div className="w-6 h-6 bg-red-700 rounded-lg mr-3 shadow-sm"></div>
                                    <div>
                                        <span className="text-gray-800 font-semibold block">GAP Crítico</span>
                                        <span className="text-xs text-gray-600">Sin cobertura</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Matrix Component with enhanced container */}
                    <div className="p-6">
                        {matrix ? (
                            <div className="bg-gray-50 rounded-xl p-4">
                                <ATTACKMatrix
                                    techniques={matrix.techniques}
                                    tactics={matrix.tactics}
                                    techniquesByTactic={matrix.techniquesByTactic}
                                    onTechniqueClick={handleTechniqueClick}
                                />
                            </div>
                        ) : (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                                <div className="flex items-center">
                                    <svg className="w-8 h-8 text-yellow-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <h3 className="text-lg font-semibold text-yellow-800">Datos no disponibles</h3>
                                        <p className="text-yellow-700 text-sm">La matriz MITRE ATT&CK no se pudo cargar. Verifique la conexión.</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Critical Gaps Section */}
            {summary && summary.criticalGaps && summary.criticalGaps.length > 0 && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Gaps Críticos de Seguridad
                            </h2>
                            <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                                Prioridad Alta
                            </div>
                        </div>
                        <div className="space-y-3">
                            {summary.criticalGaps.map((gap) => (
                                <div 
                                    key={gap.id}
                                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                                    onClick={() => handleTechniqueClick(gap.id)}
                                >
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900">{gap.name}</h3>
                                        <p className="text-sm text-gray-600">
                                            Tácticas: {gap.tactics.join(', ')}
                                        </p>
                                        <div className="mt-1 flex items-center text-xs text-gray-500">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            Requiere evaluación de controles
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-red-600 font-semibold block">Sin Cobertura</span>
                                        <span className="text-xs text-red-500">🔴 Riesgo Crítico</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Footer with metadata */}
            {metadata && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div className="text-center">
                                <div className="font-medium text-gray-700">Última Actualización</div>
                                <div className="text-gray-600">{new Date(metadata.lastUpdated).toLocaleString('es-ES')}</div>
                            </div>
                            <div className="text-center">
                                <div className="font-medium text-gray-700">Tiempo de Procesamiento</div>
                                <div className="text-gray-600">{metadata.processingTime}ms</div>
                            </div>
                            <div className="text-center">
                                <div className="font-medium text-gray-700">Técnicas Analizadas</div>
                                <div className="text-gray-600">{metadata.totalTechniques} técnicas</div>
                            </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-200 text-center text-xs text-gray-500">
                            SecureBank SOC • Powered by Cloudflare Workers • MITRE ATT&CK v15.1
                        </div>
                    </div>
                </div>
            )}

            {/* Technique Modal */}
            {selectedTechniqueId && (
                <TechniqueModal
                    techniqueId={selectedTechniqueId}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

export default DashboardPage;